import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  MenuItem,
  InputLabel,
  Select,
  IconButton,
  Box,
  FormControlProps,
  SelectChangeEvent
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Delete as DeleteIcon } from '@mui/icons-material';
import HookFormTextField from './HookFormTextField'; // Assuming this is a separate component

// Define the shape of a single income source
interface IncomeData {
  borrowerId: string;
  annualIncome: string;
  incomeDescription: string;
  employer: string;
  job_description: string;
  tenure: string;
  timeInIndustry: string;
  bfsYn: string;
}

// Define the props for the IncomeAccordion component
interface IncomeAccordionProps {
  incomeId: string;
  incomeData: IncomeData; // Corrected comment placement
  expanded: boolean;
  onChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void; // Type definition for the HOF
  onRemove: (incomeId: string) => void;
  handleIncomeChange: (incomeId: string, field: keyof IncomeData) => (event: SelectChangeEvent | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  StyledFormControl: React.ElementType<FormControlProps>; // Pass the styled component
}

const IncomeAccordion: React.FC<IncomeAccordionProps> = ({
  incomeId,
  incomeData,
  expanded,
  onChange,
  onRemove,
  handleIncomeChange,
  StyledFormControl
}) => {
  return (
    <Accordion 
      key={incomeId}
      expanded={expanded}
      onChange={onChange(incomeId)} // Call the HOF prop with the ID
      sx={{ mb: 0.5 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${incomeId}-content`}
        id={`${incomeId}-header`}
        sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', minHeight: '42px', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Typography>
            {incomeData.incomeDescription ? incomeData.incomeDescription : 'Income'}
          </Typography>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onRemove(incomeId);
            }}
            sx={{ mr: 2 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 0.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <HookFormTextField
              fieldId={`${incomeId}-annualIncome`}
              name="annualIncome"
              label="Annual Income"
              value={incomeData.annualIncome}
              // Adapt onChange for HookFormTextField if needed, assuming it passes value directly
              onChange={(value) => handleIncomeChange(incomeId, 'annualIncome')({ target: { value } } as any)} 
              InputProps={{
                startAdornment: <span>$</span>,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledFormControl fullWidth>
              <InputLabel id={`${incomeId}-incomeDescription-label`}>Income Description</InputLabel>
              <Select
                labelId={`${incomeId}-incomeDescription-label`}
                id={`${incomeId}-incomeDescription`}
                value={incomeData.incomeDescription}
                label="Income Description"
                onChange={handleIncomeChange(incomeId, 'incomeDescription')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflow: 'auto'
                    },
                  },
                }}
              >
                <MenuItem value="Full Time">Full Time</MenuItem>
                <MenuItem value="Part Time">Part Time</MenuItem>
                <MenuItem value="Hourly">Hourly</MenuItem>
                <MenuItem value="Fluctuating">Fluctuating</MenuItem>
                <MenuItem value="Seasonal">Seasonal</MenuItem>
                <MenuItem value="BFS">BFS</MenuItem>
                <MenuItem value="BFS Realtor">BFS Realtor</MenuItem>
                <MenuItem value="BFS Truck Driver">BFS Truck Driver</MenuItem>
                <MenuItem value="BFS Commission">BFS Commission</MenuItem>
                <MenuItem value="BFS Confirmable">BFS Confirmable</MenuItem>
                <MenuItem value="Maternity Leave">Maternity Leave</MenuItem>
                <MenuItem value="LTD">LTD</MenuItem>
                <MenuItem value="STD">STD</MenuItem>
                <MenuItem value="Pension">Pension</MenuItem>
                <MenuItem value="Investment">Investment</MenuItem>
                <MenuItem value="CCB">CCB</MenuItem>
                <MenuItem value="Gratuity">Gratuity</MenuItem>
                <MenuItem value="Work for Family">Work for Family</MenuItem>
                <MenuItem value="Living Allowance">Living Allowance</MenuItem>
                <MenuItem value="Foster income">Foster income</MenuItem>
                <MenuItem value="Child Support">Child Support</MenuItem>
                <MenuItem value="Alimony">Alimony</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <HookFormTextField
              fieldId={`${incomeId}-employer`}
              name="employer"
              label="Employer"
              value={incomeData.employer}
              onChange={(value) => handleIncomeChange(incomeId, 'employer')({ target: { value } } as any)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <HookFormTextField
              fieldId={`${incomeId}-job_description`}
              name="job_description"
              label="Job Description"
              value={incomeData.job_description}
              onChange={(value) => handleIncomeChange(incomeId, 'job_description')({ target: { value } } as any)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <HookFormTextField
              fieldId={`${incomeId}-tenure`}
              name="tenure"
              label="Tenure"
              value={incomeData.tenure}
              onChange={(value) => handleIncomeChange(incomeId, 'tenure')({ target: { value } } as any)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <HookFormTextField
              fieldId={`${incomeId}-timeInIndustry`}
              name="timeInIndustry"
              label="Time in Industry"
              value={incomeData.timeInIndustry}
              onChange={(value) => handleIncomeChange(incomeId, 'timeInIndustry')({ target: { value } } as any)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StyledFormControl fullWidth>
              <InputLabel id={`${incomeId}-bfsYn-label`}>BFS Y/N</InputLabel>
              <Select
                labelId={`${incomeId}-bfsYn-label`}
                value={incomeData.bfsYn}
                label="BFS Y/N"
                onChange={handleIncomeChange(incomeId, 'bfsYn')}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(IncomeAccordion);
